import React, { useEffect, useRef, useState } from "react";
import { EventBus } from "../../engine/EventBus.js";
import { PartyStore } from "../../store/PartyStore.js";
import { getCombatManager } from "../../engine/combat/combatManagerRef.js";
import { getSpellTargeting } from "../../engine/spells/spellTargetingRef.js";
import { getGridMap } from "../../engine/grid/gridMapRef.js";
import { hasLineOfSight } from "../../engine/grid/LineOfSight.js";
import { gameActor } from "../hooks/useGameMachine.js";
import { Direction } from "../../engine/types/grid.js";
import type { Position } from "../../engine/types/grid.js";
import type { SpellTargetTeam } from "../../engine/types/spells.js";
import type { Character, PlayerCharacter } from "../../engine/types/entity.js";

/**
 * S26 — Spell targeting battle board.
 *
 * Centered modal that opens when an enemy- or any-team (or AoE) spell is
 * armed. Renders a top-down grid view rotated so the party's facing is
 * "up", with the four party portraits planted at the bottom-center as a
 * visual root, a range ring around the caster, enemy markers within the
 * party's sight cone + hearing radius, and a header strip with
 * Cancel / Confirm.
 *
 * Ally-only spells (`targetTeam === "ally"`) bypass this board — they
 * route through `PartyBar`'s armed-mode portrait taps. See
 * `specs/combat-spells.md` "Wave 2 initiative — battle-board + PartyBar
 * targeting".
 */

interface ArmedState {
  spellId: string;
  spellName: string;
  casterId: string;
  casterName: string;
  casterPos: Position;
  casterFacing: Direction;
  targeting: "single" | "aoe" | "multi" | "touch";
  targetTeam: SpellTargetTeam;
  rangeCells: number;
  minTargets: number;
  maxTargets: number;
  includeDead: boolean;
}

const SIGHT_RANGE_CELLS = 8;
const SIGHT_HALF_ANGLE_DEG = 75;
const HEARING_RADIUS_CELLS = 2;

const CELL_PX = 36;

export function SpellTargetingBoard(): React.ReactElement | null {
  const [armed, setArmed] = useState<ArmedState | null>(null);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [aoePreview, setAoePreview] = useState<Position | null>(null);
  const [, force] = useState(0);
  const rafRef = useRef<number | null>(null);

  // Subscribe to arm/disarm.
  useEffect(() => {
    const offArm = EventBus.on("ui:spell_armed", (e) => {
      // Ally-only spells route to PartyBar; this board is for enemy/any/aoe.
      if (e.targetTeam === "ally" && e.targeting !== "aoe") return;
      setArmed({
        spellId: e.spellId,
        spellName: e.spellName,
        casterId: e.casterId,
        casterName: e.casterName,
        casterPos: e.casterPos,
        casterFacing: e.casterFacing,
        targeting: e.targeting,
        targetTeam: e.targetTeam,
        rangeCells: e.rangeCells,
        minTargets: e.minTargets ?? 1,
        maxTargets: e.maxTargets ?? 1,
        includeDead: e.includeDead,
      });
      setPicked([]);
      setAoePreview(null);
    });
    const offDis = EventBus.on("ui:spell_disarmed", () => {
      setArmed(null);
      setPicked([]);
      setAoePreview(null);
    });
    return () => {
      offArm();
      offDis();
    };
  }, []);

  // Hold WORLD_FREEZE while the board is up and tick the canvas at ~30fps
  // so enemy moves track live.
  useEffect(() => {
    if (!armed) return;
    gameActor.send({ type: "WORLD_FREEZE" });
    const tick = () => {
      force((n) => n + 1);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      gameActor.send({ type: "WORLD_UNFREEZE" });
    };
  }, [armed]);

  if (!armed) return null;

  const dispatcher = getSpellTargeting();
  const cm = getCombatManager();

  const cancel = () => {
    dispatcher?.cancel();
    EventBus.emit("ui:spell_disarmed", { reason: "cancelled" });
  };

  const confirm = () => {
    if (armed.targeting !== "multi") return;
    if (picked.length < armed.minTargets) return;
    dispatcher?.finalize();
  };

  const onTapEntity = (instanceId: string) => {
    if (!dispatcher) return;
    if (armed.targeting === "multi") {
      const has = picked.includes(instanceId);
      // Tap an already-selected enemy when we're at the minimum picks ⇒
      // commit. (Below min, treat the second tap as deselect so the player
      // can correct a mispick.)
      if (has && picked.length >= armed.minTargets) {
        dispatcher.finalize();
        return;
      }
      const next = has ? picked.filter((i) => i !== instanceId) : [...picked, instanceId];
      setPicked(next);
      dispatcher.commitOnEntity({ instanceId });
      // Auto-finalize at max.
      if (!has && next.length >= armed.maxTargets) {
        queueMicrotask(() => dispatcher.finalize());
      }
      return;
    }
    dispatcher.commitOnEntity({ instanceId });
  };

  const onTapCell = (cell: Position) => {
    if (!dispatcher || armed.targeting !== "aoe") return;
    if (
      aoePreview &&
      aoePreview.x === cell.x &&
      aoePreview.z === cell.z
    ) {
      dispatcher.commitOnCell(cell);
      return;
    }
    setAoePreview(cell);
    dispatcher.previewCell(cell);
  };

  const enemies = cm?.enemiesSnapshot() ?? [];
  const members = PartyStore.getMembers();
  const grid = getGridMap();
  const cellHasLOS = (cell: Position): boolean => {
    if (!grid) return true;
    if (cell.x === armed.casterPos.x && cell.z === armed.casterPos.z) return true;
    return hasLineOfSight(grid, armed.casterPos, cell);
  };
  const cellIsSolid = (cell: Position): boolean => {
    const c = grid?.getCell(cell);
    return c?.solid === true;
  };

  // Compute which cells to render — within hearing OR sight cone.
  const visibleCells = computeVisibleCells(armed.casterPos, armed.casterFacing);

  // Rotation: rotate world cells so caster facing → screen-up (-z).
  // facingVec → up, so we rotate by -facingAngle to align facing with -z.
  const rot = facingToRotation(armed.casterFacing);

  // Determine board pixel size from the visible cell extent.
  const localCells = visibleCells.map((p) => rotateAround(p, armed.casterPos, rot));
  const xs = localCells.map((p) => p.x);
  const zs = localCells.map((p) => p.z);
  const minX = Math.min(...xs, armed.casterPos.x);
  const maxX = Math.max(...xs, armed.casterPos.x);
  const minZ = Math.min(...zs, armed.casterPos.z);
  const maxZ = Math.max(...zs, armed.casterPos.z);
  const cols = maxX - minX + 1;
  const rows = maxZ - minZ + 1;
  const boardW = cols * CELL_PX;
  const boardH = rows * CELL_PX + 64; // +64 for party row at bottom
  const toScreen = (cell: Position): { left: number; top: number } => {
    const local = rotateAround(cell, armed.casterPos, rot);
    return {
      left: (local.x - minX) * CELL_PX,
      top: (local.z - minZ) * CELL_PX,
    };
  };

  const casterScreen = toScreen(armed.casterPos);
  const rangeRingPx = (armed.rangeCells + 0.5) * CELL_PX;

  // Header text.
  const headerText = (() => {
    if (armed.targeting === "multi") {
      const base = `${armed.spellName} · ${picked.length}/${armed.maxTargets}`;
      if (picked.length >= armed.minTargets) {
        return `${base} · tap a target again or CAST`;
      }
      return `${base} · pick targets`;
    }
    if (armed.targeting === "aoe") {
      return aoePreview ? `${armed.spellName} · tap again to cast` : `${armed.spellName} · pick a cell`;
    }
    return `${armed.spellName} · pick a target`;
  })();

  const confirmEnabled =
    armed.targeting === "multi" && picked.length >= armed.minTargets;

  // Enemy chips: visible + in-LOS enemies, in render order matched to pucks.
  const visibleEnemies = enemies.filter((e) => {
    const inCells = visibleCells.some(
      (c) => c.x === e.position.x && c.z === e.position.z,
    );
    return inCells && cellHasLOS(e.position);
  });

  return (
    <div
      data-component="spell-targeting-board"
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        zIndex: 50,
      }}
      onClick={cancel}
    >
      <div
        style={{
          background: "rgba(20, 24, 32, 0.95)",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 6,
          padding: 12,
          color: "#ddd",
          fontFamily: "monospace",
          minWidth: 280,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header strip */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
            gap: 12,
            fontSize: 13,
          }}
        >
          <span data-testid="targeting-header" style={{ flex: 1 }}>
            {headerText}
          </span>
          {armed.targeting === "multi" && (
            <button
              type="button"
              data-testid="targeting-confirm"
              onClick={confirm}
              disabled={!confirmEnabled}
              style={{
                padding: "6px 14px",
                background: confirmEnabled ? "#2a7a4a" : "#333",
                color: confirmEnabled ? "#dffde7" : "#666",
                border: confirmEnabled
                  ? "1px solid #6cd99a"
                  : "1px solid rgba(255,255,255,0.18)",
                boxShadow: confirmEnabled ? "0 0 8px 1px rgba(108,217,154,0.6)" : undefined,
                borderRadius: 3,
                cursor: confirmEnabled ? "pointer" : "default",
                fontFamily: "monospace",
                fontSize: 12,
                fontWeight: "bold",
                letterSpacing: 1,
              }}
            >
              CAST
            </button>
          )}
          <button
            type="button"
            data-testid="targeting-cancel"
            onClick={cancel}
            style={{
              padding: "4px 8px",
              background: "transparent",
              color: "#ccc",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 3,
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>

        {/* Enemy chip strip — synced with on-board pucks */}
        {visibleEnemies.length > 0 && (
          <div
            data-testid="enemy-chip-strip"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 8,
              maxWidth: boardW,
            }}
          >
            {visibleEnemies.map((e) => {
              const inRange = chebyshev(armed.casterPos, e.position) <= armed.rangeCells;
              const eligible = isEnemyEligible(armed, e.faction, e.hp, inRange);
              const selected = picked.includes(e.instanceId);
              const hpPct = e.maxHp > 0 ? Math.max(0, (e.hp / e.maxHp) * 100) : 0;
              return (
                <button
                  key={e.instanceId}
                  type="button"
                  data-testid={`enemy-chip-${e.instanceId}`}
                  onClick={() => {
                    if (!eligible) return;
                    onTapEntity(e.instanceId);
                  }}
                  disabled={!eligible}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 8px",
                    background: selected ? "rgba(120,60,30,0.85)" : "rgba(40,44,52,0.9)",
                    border: selected
                      ? "2px solid #ffd54a"
                      : eligible
                        ? "2px solid rgba(255,255,255,0.25)"
                        : "1px solid rgba(120,120,120,0.35)",
                    borderRadius: 4,
                    color: selected ? "#ffd0a0" : eligible ? "#ddd" : "#777",
                    fontFamily: "monospace",
                    fontSize: 11,
                    cursor: eligible ? "pointer" : "not-allowed",
                    opacity: eligible ? 1 : 0.5,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: factionColor(e.faction),
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      maxWidth: 120,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.name}
                  </span>
                  <span
                    aria-hidden
                    style={{
                      width: 30,
                      height: 4,
                      background: "#222",
                      borderRadius: 2,
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        width: `${hpPct}%`,
                        height: "100%",
                        background: hpPct > 50 ? "#4caf50" : hpPct > 25 ? "#ff9800" : "#f44336",
                      }}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Board */}
        <div
          style={{
            position: "relative",
            width: boardW,
            height: boardH,
            background: "rgba(0,0,0,0.5)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {/* Cells */}
          {visibleCells.map((cell) => {
            const { left, top } = toScreen(cell);
            const isCaster = cell.x === armed.casterPos.x && cell.z === armed.casterPos.z;
            const inRange =
              chebyshev(cell, armed.casterPos) <= armed.rangeCells;
            const inAoePreview =
              aoePreview !== null && cell.x === aoePreview.x && cell.z === aoePreview.z;
            const solid = cellIsSolid(cell);
            const losBlocked = !cellHasLOS(cell);
            const tappable =
              !solid && !losBlocked && armed.targeting === "aoe" && inRange;
            const cellColor = solid
              ? "rgba(0,0,0,0.95)"
              : losBlocked
                ? "rgba(0,0,0,0.78)"
                : isCaster
                  ? "rgba(120, 200, 255, 0.18)"
                  : inAoePreview
                    ? "rgba(255, 140, 60, 0.40)"
                    : "rgba(80, 80, 90, 0.4)";
            return (
              <div
                key={`${cell.x},${cell.z}`}
                data-testid={`board-cell-${cell.x}-${cell.z}`}
                data-los={losBlocked ? "blocked" : "open"}
                data-solid={solid ? "true" : "false"}
                onClick={() => {
                  if (!tappable) return;
                  onTapCell(cell);
                }}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width: CELL_PX - 1,
                  height: CELL_PX - 1,
                  background: cellColor,
                  border: "1px solid rgba(255,255,255,0.06)",
                  cursor: tappable ? "pointer" : "default",
                }}
              />
            );
          })}

          {/* Range ring */}
          {armed.rangeCells > 0 && (
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: casterScreen.left + CELL_PX / 2 - rangeRingPx,
                top: casterScreen.top + CELL_PX / 2 - rangeRingPx,
                width: rangeRingPx * 2,
                height: rangeRingPx * 2,
                borderRadius: "50%",
                border: "1px dashed rgba(155, 188, 214, 0.5)",
                background: "rgba(155, 188, 214, 0.06)",
                pointerEvents: "none",
              }}
            />
          )}

          {/* Enemy markers */}
          {enemies.map((e) => {
            const visible = visibleCells.some(
              (c) => c.x === e.position.x && c.z === e.position.z,
            );
            if (!visible) return null;
            // Hide if line of sight is blocked by walls.
            if (!cellHasLOS(e.position)) return null;
            const inRange = chebyshev(armed.casterPos, e.position) <= armed.rangeCells;
            const eligible = isEnemyEligible(armed, e.faction, e.hp, inRange);
            const selected = picked.includes(e.instanceId);
            const { left, top } = toScreen(e.position);
            return (
              <button
                key={e.instanceId}
                type="button"
                data-testid={`board-enemy-${e.instanceId}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  if (!eligible) return;
                  onTapEntity(e.instanceId);
                }}
                disabled={!eligible}
                title={`${e.name} ${e.hp}/${e.maxHp}`}
                style={{
                  position: "absolute",
                  left: left + 4,
                  top: top + 4,
                  width: CELL_PX - 9,
                  height: CELL_PX - 9,
                  borderRadius: "50%",
                  border: selected
                    ? "2px solid #ffd54a"
                    : eligible
                      ? "2px solid rgba(255,255,255,0.85)"
                      : "1px solid rgba(120,120,120,0.4)",
                  background: eligible ? factionColor(e.faction) : "rgba(120,120,120,0.35)",
                  cursor: eligible ? "pointer" : "default",
                  padding: 0,
                }}
              />
            );
          })}

          {/* Caster marker */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: casterScreen.left + CELL_PX / 2 - 4,
              top: casterScreen.top + CELL_PX / 2 - 4,
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#7dcfff",
              boxShadow: "0 0 4px #7dcfff",
              pointerEvents: "none",
            }}
          />

          {/* Party row planted at bottom-center as visual root */}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 8,
              display: "flex",
              justifyContent: "center",
              gap: 4,
              pointerEvents: "none",
            }}
          >
            {members.slice(0, 4).map((m) => {
              const portrait =
                (m as Partial<Character> & PlayerCharacter).portrait ?? null;
              return (
                <div
                  key={m.instanceId}
                  title={m.name}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 3,
                    background: m.hp <= 0 ? "#3a1010" : "#1a1d24",
                    border: "1px solid rgba(255,255,255,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    color: "#ccc",
                    overflow: "hidden",
                  }}
                >
                  {portrait ? (
                    <img
                      src={portrait}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    m.name.slice(0, 1).toUpperCase()
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Geometry helpers ─────────────────────────────────────────────────────────

function chebyshev(a: Position, b: Position): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.z - b.z));
}

function manhattan(a: Position, b: Position): number {
  return Math.abs(a.x - b.x) + Math.abs(a.z - b.z);
}

/** Quarter-turn count to rotate world → screen so facing points up (-z). */
function facingToRotation(f: Direction): 0 | 1 | 2 | 3 {
  switch (f) {
    case Direction.North:
      return 0;
    case Direction.East:
      return 1; // rotate world CCW by 90° so east-facing points up
    case Direction.South:
      return 2;
    case Direction.West:
      return 3;
  }
}

function rotateAround(p: Position, center: Position, turns: 0 | 1 | 2 | 3): Position {
  const dx = p.x - center.x;
  const dz = p.z - center.z;
  let nx = dx;
  let nz = dz;
  for (let i = 0; i < turns; i++) {
    const tx = nz;
    const tz = -nx;
    nx = tx;
    nz = tz;
  }
  return { x: center.x + nx, z: center.z + nz };
}

function isInSightCone(
  observer: Position,
  facing: Direction,
  target: Position,
  coneDegrees: number,
): boolean {
  const dx = target.x - observer.x;
  const dz = target.z - observer.z;
  if (dx === 0 && dz === 0) return true;
  const len = Math.sqrt(dx * dx + dz * dz);
  const f = facingVec(facing);
  const cosAngle = (dx * f.dx + dz * f.dz) / len;
  const cosThreshold = Math.cos((coneDegrees * Math.PI) / 180);
  return cosAngle + 1e-9 >= cosThreshold;
}

function facingVec(f: Direction): { dx: number; dz: number } {
  switch (f) {
    case Direction.North:
      return { dx: 0, dz: -1 };
    case Direction.East:
      return { dx: 1, dz: 0 };
    case Direction.South:
      return { dx: 0, dz: 1 };
    case Direction.West:
      return { dx: -1, dz: 0 };
  }
}

function computeVisibleCells(caster: Position, facing: Direction): Position[] {
  const out: Position[] = [];
  const r = SIGHT_RANGE_CELLS;
  for (let dx = -r; dx <= r; dx++) {
    for (let dz = -r; dz <= r; dz++) {
      const cell: Position = { x: caster.x + dx, z: caster.z + dz };
      const inHearing = manhattan(caster, cell) <= HEARING_RADIUS_CELLS;
      const inSight =
        chebyshev(caster, cell) <= SIGHT_RANGE_CELLS &&
        isInSightCone(caster, facing, cell, SIGHT_HALF_ANGLE_DEG);
      if (inHearing || inSight) out.push(cell);
    }
  }
  return out;
}

// ── Eligibility ──────────────────────────────────────────────────────────────

function isEnemyEligible(
  armed: ArmedState,
  faction: "hostile" | "neutral" | "friendly",
  hp: number,
  inRange: boolean,
): boolean {
  if (!inRange) return false;
  if (armed.includeDead && hp > 0) return false;
  if (!armed.includeDead && hp <= 0) return false;
  if (armed.targetTeam === "ally") return false;
  return faction === "hostile" || faction === "neutral";
}

function factionColor(faction: "hostile" | "neutral" | "friendly"): string {
  switch (faction) {
    case "hostile":
      return "#d04a4a";
    case "neutral":
      return "#d0a04a";
    case "friendly":
      return "#7ad07a";
  }
}
